import { useMemo } from "react";
import {
  useGetSubscriptionsQuery,
  useGetOrganizationsQuery,
  useGetPlansQuery,
} from "../../../redux/api";
import type { OrganizationSubscription } from "../../../redux/api";
import { StatCard } from "../../../components/StatCard";
import { IconChart, IconReport, IconCheck, IconClock } from "../../../components/icons";

const money = (v: number) => v.toLocaleString();

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : "—";

// Platform Owner → Revenue. Platform-wide revenue analytics from every
// organization subscription.
const Revenue = () => {
  const { data: subscriptions = [], isLoading } = useGetSubscriptionsQuery();
  const { data: organizations = [] } = useGetOrganizationsQuery();
  const { data: plans = [] } = useGetPlansQuery();

  const orgName = (id: string) =>
    organizations.find((o) => o.id === id)?.name ?? id;
  const planName = (id: string) =>
    plans.find((p) => p.id === id)?.name ?? id;

  const stats = useMemo(() => {
    const revenue = subscriptions.reduce(
      (s, x) => s + (x.amountPaid ?? 0),
      0,
    );
    const active = subscriptions.filter(
      (s) => (s.status ?? "").toLowerCase() === "active",
    ).length;
    const pending = subscriptions.filter((s) =>
      ["pending", "suspended"].includes((s.status ?? "").toLowerCase()),
    ).length;
    return { revenue, active, pending, count: subscriptions.length };
  }, [subscriptions]);

  // Revenue grouped by plan.
  const byPlan = useMemo(() => {
    const totals = new Map<string, { count: number; revenue: number }>();
    subscriptions.forEach((s) => {
      const key = s.planId ?? "—";
      const cur = totals.get(key) ?? { count: 0, revenue: 0 };
      cur.count += 1;
      cur.revenue += s.amountPaid ?? 0;
      totals.set(key, cur);
    });
    return [...totals.entries()]
      .map(([planId, v]) => ({ planId, ...v }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [subscriptions]);

  const recent = useMemo(
    () =>
      [...subscriptions]
        .sort((a, b) => (b.startDate ?? "").localeCompare(a.startDate ?? ""))
        .slice(0, 8),
    [subscriptions],
  );

  return (
    <div className="dashboard">
      <div className="stat-grid">
        <StatCard
          label="Total revenue"
          value={isLoading ? "…" : money(stats.revenue)}
          tone="success"
          icon={<IconChart />}
        />
        <StatCard
          label="Subscriptions"
          value={stats.count}
          tone="primary"
          icon={<IconReport />}
        />
        <StatCard
          label="Active"
          value={stats.active}
          tone="success"
          icon={<IconCheck />}
        />
        <StatCard
          label="Pending / suspended"
          value={stats.pending}
          tone="warning"
          icon={<IconClock />}
        />
      </div>

      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Revenue by plan</h2>
            <p className="panel__sub">{byPlan.length} plan(s)</p>
          </div>
        </div>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Subscriptions</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {byPlan.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={3}>
                    No subscription revenue yet.
                  </td>
                </tr>
              ) : (
                byPlan.map((row) => (
                  <tr key={row.planId}>
                    <td className="cell-title">{planName(row.planId)}</td>
                    <td>{row.count}</td>
                    <td className="cell-date">{money(row.revenue)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Recent subscriptions</h2>
            <p className="panel__sub">Latest {recent.length}</p>
          </div>
        </div>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Start</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={5}>
                    No subscriptions yet.
                  </td>
                </tr>
              ) : (
                recent.map((s: OrganizationSubscription) => (
                  <tr key={s.id}>
                    <td className="cell-title">{orgName(s.organizationId)}</td>
                    <td>
                      <span className="pill pill--neutral">
                        {planName(s.planId)}
                      </span>
                    </td>
                    <td className="cell-date">
                      {s.currency} {money(s.amountPaid ?? 0)}
                    </td>
                    <td>{s.status}</td>
                    <td className="cell-date">{formatDate(s.startDate)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default Revenue;
